import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  Flame, Snowflake, Droplets, Wrench, ShieldCheck, CalendarClock, ChevronRight, ChevronDown, Info,
  AlertTriangle, RefreshCw, Ban, RotateCcw, User, Sparkles, Award, FileText, ArrowRight, Minus, Plus, Clock,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { CustomerPicker, type PickedCustomer } from "@/components/CustomerPicker";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import {
  buildAgreementView, type AgreementRow, type AgrTaskRow, type AgrPartRow,
  type AgreementView, type AgrStatus, type AgrTaskView,
} from "@/lib/agreement";
import {
  useAgreementVisits, useAgreementOverrides, useSetVisitOverride, useClearVisitOverride,
  useAgreementSchedule,
  type VisitOverride, type OverrideStatus, type SetOverrideInput,
} from "@/features/agreements/api";
import {
  useAgreementActions, useRecordAgreementAction, useUndoAgreementAction,
  addContractPeriod, type AgreementActionRow, type AgreementActionType,
} from "@/features/agreements/lifecycle";
import { useScheduleTuneup, type TuneupDueRow } from "@/features/agreements/obligations";
import { useCustomerEquipment } from "./CustomerEquipment";
import { useEquipmentOverrides } from "./equipmentOverrides";
import { WhatsCovered, PlanAdjustmentsLedger, PlanGlance } from "./WhatsCovered";
import { checkCategories } from "@/features/agreements/planCoverage";
import { buildEquipmentView, type EquipCategory } from "@/lib/equipment";
import { useNavigateWithBack } from "@/lib/navBack";
import { PlanBuilderModal } from "@/features/agreements/PlanBuilder";
import { createPlanInvoice } from "@/features/agreements/planPricing";
import { usePlanVisitCredit, fmtShort } from "@/features/agreements/planCredit";
import { useIncludedParts, type IncludedPart } from "./PlanAccessoriesEditor";
import { planPartKind, type PartKind } from "@/lib/truckParts";
import { ManagePlanModal } from "./ManagePlanModal";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { repairPctForTerm } from "@/features/agreements/planPricing";
import { usePlanDiscounts } from "./serviceTypes";
import { InfoTip as HelpTip } from "@/components/InfoTip";

interface Props {
  custno: string;
  locno?: string | null;
}

function useCustomerAgreements(custno: string | undefined, locno?: string | null) {
  return useQuery({
    queryKey: ["customer_agreements", custno, locno ?? "all"],
    queryFn: async (): Promise<AgreementView[]> => {
      if (!custno) return [];
      const agrSel =
        "custno, locno, agrmt_no, agrmt_type, contr_per, contr_amt, dept, orig_contr, beg_date, renew_date, expire_date, last_srvc, next_post_date, sa_inactive, notes";
      let aq = supabase.from("mirror_serv_agr").select(agrSel).eq("custno", custno);
      if (locno) aq = aq.eq("locno", locno);
      const { data: agrs, error } = await aq;
      if (error) throw error;
      const agreements = (agrs ?? []) as AgreementRow[];
      if (agreements.length === 0) return [];

      // MEMBERSHIP-ESC-FULFILLED: the SQL lifecycle says which inactive
      // agreements are "all checks used, paid through" rather than cancelled
      // (it reads ESC's task numbering AND the dispatch evidence).
      const [{ data: tasks }, { data: parts }, { data: lifecycles }] = await Promise.all([
        supabase.from("mirror_agr_task")
          .select("agrmt_no, master_task, counter, task, time_min, long_desc, sub_total")
          .eq("custno", custno),
        supabase.from("mirror_agr_parts")
          .select("agrmt_no, master_task, part, quantity")
          .eq("custno", custno),
        supabase.from("v_membership_plan")
          .select("agrmt_no, status")
          .eq("custno", custno)
          .eq("source", "esc"),
      ]);
      const fulfilledNos = new Set(
        ((lifecycles ?? []) as { agrmt_no: string; status: string }[]).filter((l) => l.status === "fulfilled").map((l) => l.agrmt_no),
      );
      const judged = new Set(((lifecycles ?? []) as { agrmt_no: string }[]).map((l) => l.agrmt_no));
      const tByAgr = new Map<string, AgrTaskRow[]>();
      for (const t of (tasks ?? []) as (AgrTaskRow & { agrmt_no: string })[]) {
        if (!tByAgr.has(t.agrmt_no)) tByAgr.set(t.agrmt_no, []);
        tByAgr.get(t.agrmt_no)!.push(t);
      }
      const pByAgr = new Map<string, AgrPartRow[]>();
      for (const p of (parts ?? []) as (AgrPartRow & { agrmt_no: string })[]) {
        if (!pByAgr.has(p.agrmt_no)) pByAgr.set(p.agrmt_no, []);
        pByAgr.get(p.agrmt_no)!.push(p);
      }

      const views = agreements.map((a) =>
        buildAgreementView(a, tByAgr.get(a.agrmt_no) ?? [], pByAgr.get(a.agrmt_no) ?? [],
          // The SQL verdict when the view has judged this agreement; otherwise
          // buildAgreementView falls back to ESC's own "n of n" numbering.
          judged.has(a.agrmt_no) ? { fulfilled: fulfilledNos.has(a.agrmt_no) } : undefined),
      );
      // In-force first (a fulfilled plan is in force — paid through, nothing left to deliver), then by expiry desc.
      const rank: Record<AgrStatus, number> = { active: 0, expiring: 1, fulfilled: 1, expired: 3, inactive: 4 };
      views.sort((x, y) => rank[x.status] - rank[y.status] || (y.expire_date ?? "").localeCompare(x.expire_date ?? ""));
      return views;
    },
    enabled: !!custno,
    staleTime: 60 * 1000,
  });
}

interface AgrExtra {
  salesperson: string | null;
  accessories: string[];
  covered_equipment: { mfg: string | null; model: string | null }[];
  /** AGR-TUNEUPS-02: per-TERM counts — used = completed this term, open =
   *  booked-not-done this term (never counted as used), next_label = the
   *  office's "1 of 2" wording for the next check. */
  tuneups: { used: number | null; entitled: number | null; open?: number | null; next_label?: string | null } | null;
}

// Salesperson + accessories + plan-linked equipment that the structured ESC
// task/part tables don't carry. agreement_detail mines them: salesman code →
// mirror_employee name; accessory codes + tune-up "X of Y" from the dispatch notes.
function useAgreementExtras(custno: string | undefined, locno?: string | null) {
  return useQuery({
    queryKey: ["agreement_extras", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Map<string, AgrExtra>> => {
      // 360-AUDIT wave 2 (G): a dropped error here rendered plans WITHOUT their
      // accessories/tune-up counts — looking legitimately empty. Throw instead.
      const { data, error } = await supabase.rpc("agreement_detail", { p_custno: custno, p_locno: locno ?? null });
      if (error) throw error;
      const m = new Map<string, AgrExtra>();
      const ags = (data as { agreements?: Array<{ agrmt_no: string; salesperson: string | null; accessories: string[]; covered_equipment: { mfg: string | null; model: string | null }[]; tuneups: { used: number | null; entitled: number | null; open?: number | null; next_label?: string | null } | null }> } | null)?.agreements ?? [];
      for (const ag of ags) m.set(String(ag.agrmt_no), { salesperson: ag.salesperson ?? null, accessories: ag.accessories ?? [], covered_equipment: ag.covered_equipment ?? [], tuneups: ag.tuneups ?? null });
      return m;
    },
  });
}

// ---------------------------------------------------------------------------
// PLAN-CARD-01 (34i91, the Membership mock): ONE header for an ESC agreement
// and a Pegasus-sold plan. The term is the name ("3-Year Plan"), the money
// reads in words ("$808.35 for 3 years · $269/yr"), the ESC number is a chip,
// Manage Plan and Price & Build sit together, and five facts carry what the
// office asks first — Sold By · Last Renewed · Tune-Ups This Term · Next Check
// · Expires. Measurements from the mock: name 24px, facts 15px, body 14px.
// ---------------------------------------------------------------------------
type FactTone = "good" | "warn" | "mute";
interface PlanFactSpec { icon: LucideIcon; label: string; value: string; sub?: string | null; tone?: FactTone }

function PlanFact({ icon: Icon, label, value, sub, tone }: PlanFactSpec) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[.1em] text-slate-400">
        <Icon className="h-3 w-3 shrink-0" /> {label}
      </div>
      <div className={cn("mt-1 truncate text-[15px] font-bold tabular-nums", tone === "good" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : tone === "mute" ? "text-slate-500" : "text-slate-900")}>{value}</div>
      {sub ? <div className="text-xs leading-snug text-slate-500">{sub}</div> : null}
    </div>
  );
}

function StatePill({ tone, children }: { tone: "good" | "warn" | "mute" | "bad"; children: ReactNode }) {
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold",
      tone === "good" && "bg-emerald-50 text-emerald-700",
      tone === "warn" && "bg-amber-50 text-amber-700",
      tone === "bad" && "bg-rose-50 text-rose-700",
      tone === "mute" && "bg-slate-100 text-slate-500",
    )}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

function PlanHeader({ name, active, status, chip, moneyLine, facts, onManage, onBuild, canEdit }: {
  name: string;
  active: boolean;
  status: { tone: "good" | "warn" | "mute" | "bad"; label: string };
  chip: ReactNode;
  moneyLine: ReactNode;
  facts: PlanFactSpec[];
  onManage: () => void;
  onBuild?: () => void;
  canEdit: boolean;
}) {
  return (
    <div className="px-6 pt-5">
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="flex min-w-0 items-center gap-4">
          <span className={cn("flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl text-white", active ? "bg-gradient-to-br from-emerald-400 to-emerald-700" : "bg-gradient-to-br from-slate-300 to-slate-500")}>
            <ShieldCheck className="h-7 w-7" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-display text-2xl font-extrabold leading-none tracking-tight text-slate-900">{name}</h2>
              <StatePill tone={status.tone}>{status.label}</StatePill>
              {chip}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-sm text-slate-600">{moneyLine}</div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2.5">
            <Button variant="secondary" size="lg" onClick={onManage}><RefreshCw className="h-4 w-4" /> Manage Plan</Button>
            {onBuild && <Button size="lg" onClick={onBuild}><Sparkles className="h-4 w-4" /> Price &amp; Build</Button>}
          </div>
        )}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-x-5 gap-y-4 border-b border-slate-100 pb-5 sm:grid-cols-3 xl:grid-cols-5">
        {facts.map((f) => <PlanFact key={f.label} {...f} />)}
      </div>
    </div>
  );
}

const Dot = () => <span className="text-slate-300">·</span>;

function longDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${MONTHS[parseInt(m, 10) - 1] ?? m} ${parseInt(d, 10)}, ${y}`;
}
/** "3 Year" / "3YR" → 3. */
function termYearsOf(contrPer: string | null, agrmtType?: string | null): number | null {
  const n = parseInt(contrPer ?? "", 10);
  if (Number.isFinite(n) && n > 0) return n;
  const t = (agrmtType ?? "").match(/^(\d+)YR$/i);
  return t ? parseInt(t[1], 10) : null;
}
function termWords(years: number | null): string {
  if (!years) return "the term";
  return years === 1 ? "1 year" : `${years} years`;
}
function perYearWord(total: number | null, years: number | null): string | null {
  if (!total || !years || years <= 1) return null;
  return `${Math.round(total / years).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}/yr`;
}
function monthsUntil(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso + "T00:00:00");
  const now = new Date();
  return (then.getFullYear() - now.getFullYear()) * 12 + (then.getMonth() - now.getMonth());
}
function seasonOf(iso: string): string {
  const m = parseInt(iso.slice(5, 7), 10);
  const y = iso.slice(0, 4);
  const s = m >= 3 && m <= 5 ? "Spring" : m >= 6 && m <= 8 ? "Summer" : m >= 9 && m <= 11 ? "Fall" : "Winter";
  return `${s} ${y}`;
}
/** The systems a plan's checks cover, as the name line says it: "Furnace & A/C". */
function systemsWord(labels: string[]): string | null {
  const seen: string[] = [];
  for (const l of labels) {
    const w = l.replace(/ (tune-up|check)$/i, "");
    if (!seen.includes(w)) seen.push(w);
  }
  return seen.length ? seen.join(" & ") : null;
}
/** The plan's rhythm, from its checks: "furnace each fall · A/C each spring". */
function rhythmWord(labels: string[]): string | null {
  const parts: string[] = [];
  const has = (re: RegExp) => labels.some((l) => re.test(l));
  if (has(/^Furnace/)) parts.push("furnace each fall");
  if (has(/^Boiler/)) parts.push("boiler each fall");
  if (has(/^A\/C/)) parts.push("A/C each spring");
  if (has(/^Mini/)) parts.push("mini-split each spring");
  if (has(/^Plumbing/)) parts.push("plumbing yearly");
  return parts.length ? parts.join(" · ") : null;
}

// Per-check visuals. Furnace/Boiler checks read WARM (orange) to set them apart
// from the cool A/C check at a glance (Seth 2026-06-01). Single source — used
// everywhere a plan-check icon renders (Included visits, Upcoming visits, and
// the Service-under-plan history).
function checkVisual(label: string): { Icon: LucideIcon; fg: string; bg: string } {
  if (label.startsWith("Furnace") || label.startsWith("Boiler")) return { Icon: Flame, fg: "text-orange-600", bg: "bg-orange-50" };
  if (label.startsWith("A/C") || label.startsWith("Mini")) return { Icon: Snowflake, fg: "text-brand-600", bg: "bg-brand-50" };
  if (label.startsWith("Plumbing")) return { Icon: Droplets, fg: "text-brand-600", bg: "bg-brand-50" };
  return { Icon: Wrench, fg: "text-brand-600", bg: "bg-brand-50" };
}
function money(v: number | null): string {
  if (v === null) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function fmt(d: string | null): string {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${m}/${day}/${y}`;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthYear(iso: string | null): string {
  if (!iso) return "—";
  const [y, m] = iso.split("-");
  return `${MONTHS[parseInt(m, 10) - 1] ?? m} ${y}`;
}
function yearsSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  const now = new Date();
  let y = now.getFullYear() - then.getFullYear();
  const m = now.getMonth() - then.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < then.getDate())) y -= 1;
  return Math.max(0, y);
}
function monthsSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  const now = new Date();
  return Math.max(0, (now.getFullYear() - then.getFullYear()) * 12 + (now.getMonth() - then.getMonth()));
}
// Annual checks are "due" if the last one was >13 months ago (or never).
function monthsAgoISO(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}
// Start of the customer's current service year = most recent anniversary of the
// term start (renew/beg date) on/before today. Used for the confident
// "X of N done this service year" count.
function serviceYearStartISO(termStart: string): string {
  const [, m, d] = termStart.split("-").map((s) => parseInt(s, 10));
  const today = new Date();
  let year = today.getFullYear();
  const anniv = new Date(year, m - 1, d);
  if (anniv.getTime() > today.getTime()) year -= 1;
  return `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
export function CustomerAgreements({ custno, locno }: Props) {
  const { data, isLoading } = useCustomerAgreements(custno, locno);
  const { data: extras } = useAgreementExtras(custno, locno);
  const { data: pegAgs } = usePegasusAgreements(custno, locno);
  const { data: pegQuotes } = usePegasusQuotes(custno, locno);
  const [builderOpen, setBuilderOpen] = useState(false);
  // PLAN-CREDIT-14: a visit paid in full in the last 14 days earns its member savings
  // off a plan — the office sees it here; the homeowner page never advertises it.
  const { data: visitCredit } = usePlanVisitCredit(custno, 3);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 1 }).map((_, i) => <Card key={i} className="h-44 animate-pulse bg-slate-100" />)}
      </div>
    );
  }
  const hasEsc = !!data && data.length > 0;
  const hasPeg = !!pegAgs && pegAgs.length > 0;
  const openBuilder = () => setBuilderOpen(true);
  return (
    <div className="space-y-3">
      {pegQuotes && pegQuotes.length > 0 && <SavedQuotesCard quotes={pegQuotes} />}
      {/* PLAN-CARD-01: Price & Build rides on the card beside Manage Plan (Seth
          2026-10-21: "move the build & price agreement button next to manage
          plan — increase those"); the no-plan state keeps its own. */}
      {hasPeg && pegAgs!.map((ag, i) => <PegasusAgreementCard key={ag.id} ag={ag} custno={custno} onBuild={openBuilder} priorPlan={hasEsc || i < pegAgs!.length - 1} />)}
      {hasEsc && data!.map((a) => <AgreementCard key={`${a.locno}-${a.agrmt_no}`} a={a} extra={extras?.get(a.agrmt_no)} onBuild={openBuilder} />)}
      {!hasEsc && !hasPeg && (
        <Card className="flex flex-col items-center gap-4 p-10 text-center">
          <span className="flex h-[52px] w-[52px] items-center justify-center rounded-2xl bg-gradient-to-br from-slate-200 to-slate-400 text-white"><ShieldCheck className="h-7 w-7" /></span>
          <div>
            <div className="font-display text-lg font-extrabold tracking-tight text-slate-900">No Plan on File</div>
            <div className="mt-1 text-sm text-slate-500">Price one in a minute — term, systems, filters.</div>
          </div>
          {visitCredit && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-2.5 text-left text-sm text-emerald-900">
              <div className="font-semibold">Recent Visit Credit · {money(visitCredit.credit)} off a plan through {fmtShort(visitCredit.until)}</div>
              <div className="text-xs text-emerald-800/80">{fmtShort(visitCredit.date)} visit · {money(visitCredit.total)} paid in full · the member diagnostic{visitCredit.repair_savings > 0 ? ` and ${Math.round(visitCredit.pct * 100)}% off the repair` : ""} they'd have had on a 3-year plan</div>
            </div>
          )}
          <Button size="lg" onClick={openBuilder}><Sparkles className="h-4 w-4" /> Price &amp; Build</Button>
        </Card>
      )}
      {builderOpen && <PlanBuilderModal custno={custno} locno={locno} onClose={() => setBuilderOpen(false)} />}
    </div>
  );
}

// PLAN-HISTORY — the membership story, derived from the agreement's own ESC
// dates (orig_contr = first signed; renew_date = last renewed; expire_date =
// current term end). No manual flag: tenure, last-renewed, and active/lapsed
// all fall out of the dates ESC already keeps. A lapsed member surfaces as a
// win-back signal (the amber "Lapsed N mo ago" pill).
// The agreement's real billing + visit history, from its ESC invoices
// (mirror_sales tagged with the agrmt_no). ESC keeps no renewal-history on the
// agreement itself, but the invoices DO: a non-trivial amount = a renewal/charge,
// $0 = a covered tune-up visit. Lazy — only fetched when the section is expanded.
interface AgrBill { invoice: string; date: string | null; amount: number; dispatch: string | null }
function useAgreementBilling(custno: string | null | undefined, agrmtNo: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["agr_billing", custno, agrmtNo],
    enabled: !!custno && !!agrmtNo && enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<AgrBill[]> => {
      const { data, error } = await supabase.from("mirror_sales")
        .select("invoice, inv_date, inv_amount, dispatch")
        .eq("custno", custno as string).eq("agrmt_no", agrmtNo as string)
        .order("inv_date", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((r: Record<string, unknown>) => ({
        invoice: r.invoice as string,
        date: (r.inv_date as string) ?? null,
        amount: Number(r.inv_amount) || 0,
        dispatch: (r.dispatch as string) ?? null,
      }));
    },
  });
}

// TERM-TIMELINE (Seth 2026-08-18: "I'm still not sure from the timeline if
// there were any gaps in coverage. What terms were renewed and how long and
// when"): real term SEGMENTS derived from the agreement's renewal invoices
// (each renewal-sized payment starts a term; term length from the contract
// period). Gaps between an expected term end and the next renewal render
// amber. Honest edges: invoices before the mirror window show as a hatched
// "early years" segment off orig_contr, and everything is derived — hover any
// segment for its dates + amount.
interface TermSeg {
  kind: "early" | "term" | "gap" | "current" | "lapsed";
  start: string;
  end: string;
  amount: number | null;
}
function deriveTermSegments(opts: {
  renewals: { date: string; amount: number }[];
  termYears: number | null;
  joined: string | null;
  expire: string | null;
  active: boolean;
}): { segs: TermSeg[]; gaps: number } {
  const { renewals, termYears, joined, expire, active } = opts;
  const today = new Date().toISOString().slice(0, 10);
  const addYears = (iso: string, y: number) => {
    const d = new Date(iso + "T00:00:00");
    d.setFullYear(d.getFullYear() + y);
    return d.toISOString().slice(0, 10);
  };
  const addDays = (iso: string, n: number) => {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const segs: TermSeg[] = [];
  let gaps = 0;
  const ty = termYears && termYears > 0 ? termYears : 1;
  if (renewals.length === 0) {
    if (joined) {
      segs.push({ kind: active ? "current" : "term", start: joined, end: expire ?? addYears(joined, ty), amount: null });
    }
  } else {
    // Years before the first mirrored renewal invoice — coverage we know
    // existed (orig_contr) but whose invoices predate the mirror.
    if (joined && joined < addDays(renewals[0].date, -90)) {
      segs.push({ kind: "early", start: joined, end: renewals[0].date, amount: null });
    }
    for (let i = 0; i < renewals.length; i++) {
      const r = renewals[i];
      const expectedEnd = addYears(r.date, ty);
      const next = renewals[i + 1] ?? null;
      const isLast = !next;
      if (isLast) {
        const end = expire && expire > r.date ? expire : expectedEnd;
        segs.push({ kind: active ? "current" : "term", start: r.date, end, amount: r.amount });
        if (!active && end < today) {
          segs.push({ kind: "lapsed", start: end, end: today, amount: null });
        }
      } else if (next.date > addDays(expectedEnd, 45)) {
        segs.push({ kind: "term", start: r.date, end: expectedEnd, amount: r.amount });
        segs.push({ kind: "gap", start: expectedEnd, end: next.date, amount: null });
        gaps++;
      } else {
        // Renewed at (or a touch before/after) term end — continuous.
        segs.push({ kind: "term", start: r.date, end: next.date, amount: r.amount });
      }
    }
  }
  return { segs, gaps };
}

function MembershipTimeline({ joined, expire, active, period, custno, agrmtNo, contrAmt }: {
  joined: string | null;
  renewed: string | null;
  expire: string | null;
  active: boolean;
  period?: string | null;
  custno?: string | null;
  agrmtNo?: string | null;
  contrAmt?: number | null;
}) {
  const navigate = useNavigateWithBack();
  const [open, setOpen] = useState(false);
  // 360-AUDIT #6: Pegasus-native plans have no ESC billing ledger — without
  // custno+agrmtNo the expand showed "Loading…" forever. No ledger, no expand.
  const hasLedger = Boolean(custno && agrmtNo);
  // Eager: the term segments need the billing history up front.
  const { data: billing } = useAgreementBilling(custno, agrmtNo, true);
  const tenure = yearsSince(joined);
  const lapsedMonths = !active && expire ? monthsSince(expire) : null;
  const termYears = period ? (parseInt(period, 10) || null) : null;
  const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const fullDate = (d: string | null) => { if (!d) return "—"; const [y, m, dd] = d.slice(0, 10).split("-"); return `${m}/${dd}/${y}`; };
  const renewalThresh = contrAmt ? contrAmt * 0.5 : 200;
  const renewalRows = (billing ?? [])
    .filter((b) => b.amount >= renewalThresh && !!b.date)
    .map((b) => ({ date: b.date!, amount: b.amount }))
    .sort((a, b) => a.date.localeCompare(b.date));
  const renewals = renewalRows.length;
  const { segs, gaps } = deriveTermSegments({
    renewals: renewalRows,
    termYears,
    joined,
    expire,
    active,
  });

  // CLICK-TO-PIN (Seth 2026-08-18: hover shouldn't be the only way to read
  // the timeline). Default pin = the current/last term so the detail row is
  // never empty; clicking any block switches it, clicking again unpins back
  // to the default.
  const [selSeg, setSelSeg] = useState<number | null>(null);
  const defaultSegIdx = (() => {
    const cur = segs.findIndex((s) => s.kind === "current");
    return cur >= 0 ? cur : segs.length > 0 ? segs.length - 1 : null;
  })();
  const pinnedIdx = selSeg ?? defaultSegIdx;
  const pinned = pinnedIdx != null ? (segs[pinnedIdx] ?? null) : null;
  const monthsBetween = (a: string, b: string) => {
    const [ay, am] = a.split("-").map((x) => parseInt(x, 10));
    const [by, bm] = b.split("-").map((x) => parseInt(x, 10));
    return Math.max(0, (by - ay) * 12 + (bm - am));
  };
  const segLine = (s: TermSeg): string => {
    if (s.kind === "gap")
      return `No coverage on record ${monthYear(s.start)} – ${monthYear(s.end)} — about ${monthsBetween(s.start, s.end)} months between the expected term end and the next renewal.`;
    if (s.kind === "lapsed") return `Lapsed since ${monthYear(s.start)} — a renewal conversation is warranted.`;
    if (s.kind === "early")
      return `Member since ${monthYear(s.start)} — renewal invoices before ${monthYear(s.end)} predate the Pegasus mirror, so those early terms can't be drawn.`;
    return `${monthYear(s.start)} – ${monthYear(s.end)}${termYears ? ` · ${termYears}-year term` : ""}${s.amount != null ? ` · renewed for ${usd(s.amount)}` : ""}${s.kind === "current" ? " · current term" : ""}`;
  };

  return (
    <div className="px-4 pt-4">
      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-brand-50/70 via-white to-white p-3.5">
        <button type="button" onClick={() => hasLedger && setOpen((o) => !o)} className={hasLedger ? "w-full cursor-pointer text-left" : "w-full cursor-default text-left"} title={hasLedger ? "Show the full billing & visit history" : "Billing history for Pegasus-sold plans lives under Payments"}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
                <Award className="h-5 w-5" />
              </span>
              <div className="leading-tight">
                <div className="text-sm font-bold text-slate-900">
                  {tenure != null ? `${tenure} ${tenure === 1 ? "year" : "years"} a Plus member` : "Plus member"}
                </div>
                <div className="text-xs text-slate-500">Joined {monthYear(joined)}{termYears ? ` · ${termYears}-year plan` : period ? ` · ${period} plan` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                  active ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
                )}
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-emerald-500" : "bg-amber-500")} />
                {active
                  ? `Active through ${monthYear(expire)}`
                  : expire
                    ? `Lapsed ${monthYear(expire)}${lapsedMonths != null ? ` · ${lapsedMonths} mo ago` : ""}`
                    : "Inactive"}
              </span>
              {hasLedger && <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />}
            </div>
          </div>
        </button>

        {/* TERM SEGMENTS — each block is a real contract term (derived from
            its renewal invoice); amber = a coverage gap; hatched = years
            before the invoice mirror. CLICK a block to read it (Seth
            2026-08-18: hover shouldn't be the only way) — details pin below.
            Lives OUTSIDE the header toggle so segment clicks don't fight it. */}
        {segs.length > 0 && (
          <div className="mt-3">
            <div className="flex h-4 w-full items-stretch gap-0.5 overflow-hidden rounded-full">
              {segs.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelSeg(i === selSeg ? null : i)}
                  title={segLine(s)}
                  className={cn(
                    "min-w-[14px] flex-1 transition-all focus-visible:outline-none",
                    s.kind === "term" && "bg-emerald-200 hover:bg-emerald-300",
                    s.kind === "current" && "bg-emerald-500 hover:bg-emerald-600",
                    s.kind === "gap" && "bg-amber-300 hover:bg-amber-400",
                    s.kind === "lapsed" && "bg-amber-200 hover:bg-amber-300",
                    s.kind === "early" &&
                      "bg-[repeating-linear-gradient(45deg,#e2e8f0_0,#e2e8f0_4px,#f8fafc_4px,#f8fafc_8px)] hover:opacity-80",
                    selSeg === i && "ring-2 ring-inset ring-slate-700/60",
                  )}
                />
              ))}
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px]">
              <span className="font-medium text-slate-500">{monthYear(joined)}</span>
              <span className="text-slate-400">
                {renewals > 0 && `${renewals} renewal${renewals === 1 ? "" : "s"} on record`}
                {gaps > 0 ? (
                  <span className="font-semibold text-amber-600"> · {gaps} coverage gap{gaps === 1 ? "" : "s"}</span>
                ) : renewals > 0 ? (
                  <span className="text-emerald-600"> · no gaps</span>
                ) : null}
              </span>
              <span className={cn("font-medium", active ? "text-emerald-600" : "text-amber-600")}>
                {active ? monthYear(expire) : `expired ${monthYear(expire)}`}
              </span>
            </div>
            {/* Pinned detail for the selected (or current) segment — always
                readable, no hover required. */}
            {pinned && (
              <div
                className={cn(
                  "mt-2 flex items-start gap-2 rounded-lg px-3 py-2 text-[13px]",
                  pinned.kind === "gap" || pinned.kind === "lapsed"
                    ? "bg-amber-50 text-amber-800"
                    : "bg-white/70 text-slate-700",
                )}
              >
                <span
                  className={cn(
                    "mt-1 h-2.5 w-2.5 shrink-0 rounded-sm",
                    pinned.kind === "term" && "bg-emerald-200",
                    pinned.kind === "current" && "bg-emerald-500",
                    (pinned.kind === "gap" || pinned.kind === "lapsed") && "bg-amber-300",
                    pinned.kind === "early" && "bg-slate-200",
                  )}
                />
                <span className="min-w-0">{segLine(pinned)}</span>
              </div>
            )}
          </div>
        )}

        {open && (
          <div className="mt-3 border-t border-slate-200 pt-3">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Billing &amp; visit history
                <HelpTip label="About this history">From ESC invoices on this agreement. $0 lines are covered tune-up visits; the rest are renewals and charges. Click any to open it.</HelpTip>
              </span>
              {billing && billing.length > 0 && <span className="text-[11px] text-slate-400">{renewals} renewal{renewals === 1 ? "" : "s"} · {billing.length} invoices</span>}
            </div>
            {!billing ? (
              <div className="py-2 text-xs text-slate-400">Loading…</div>
            ) : billing.length === 0 ? (
              <div className="py-2 text-xs text-slate-400">No invoices on file for this agreement yet.</div>
            ) : (
              <div className="max-h-64 space-y-0.5 overflow-auto">
                {billing.map((b) => {
                  const isRenewal = b.amount >= renewalThresh;
                  return (
                    <button key={b.invoice} onClick={() => navigate(`/invoice/${b.invoice}`)}
                      className="flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-white" title={`Open invoice #${b.invoice}`}>
                      <span className="w-20 shrink-0 text-xs tabular-nums text-slate-500">{fullDate(b.date)}</span>
                      <span className="min-w-0 flex-1 text-xs">
                        {b.amount === 0
                          ? <span className="text-slate-500">Covered tune-up visit</span>
                          : isRenewal
                            ? <span className="font-semibold text-brand-700">Renewal</span>
                            : <span className="text-slate-600">Charge</span>}
                        {b.dispatch && <span className="ml-1 text-[11px] text-slate-400">· dispatch {b.dispatch}</span>}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-slate-700">{b.amount === 0 ? "—" : usd(b.amount)}</span>
                      <ArrowRight className="h-3 w-3 shrink-0 text-slate-300" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// AGR-NEXT-2 — SAVED QUOTES (priced but not yet sold) + promote-to-agreement.
// A quote is a pegasus_plan_sale with status='quote' (the builder's "Save
// quote"). Selling it flips status→sold, stamps the term dates, and generates
// the tune-up obligations — the SAME path a fresh sale takes, so a promoted
// quote behaves identically to one sold outright.
interface PegasusQuote {
  id: string; custno: string; locno: string | null; term: number;
  base_id: string | null; base_name: string | null; total: number; second_system: boolean; created_at: string;
  credit_note?: string | null;
}

function usePegasusQuotes(custno: string | undefined, locno?: string | null) {
  return useQuery({
    queryKey: ["pegasus_quotes", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PegasusQuote[]> => {
      let q = supabase.from("pegasus_plan_sale")
        .select("id, custno, locno, term, base_id, base_name, total, second_system, created_at, credit_note")
        .eq("custno", custno as string).eq("status", "quote").order("created_at", { ascending: false });
      if (locno) q = q.eq("locno", locno);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as PegasusQuote[];
    },
  });
}

function usePromoteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ quote, startDate }: { quote: PegasusQuote; startDate: string }) => {
      // PLAN-SALE-INVOICE: a saved quote sells the same way the Plan Builder does —
      // its own invoice, the row ACCEPTED against it, ACTIVE when the invoice is paid
      // (the chosen start date rides on the row; activation honours it).
      const invoiceId = await createPlanInvoice({ custno: quote.custno, locno: quote.locno, baseName: quote.base_name, term: quote.term, total: Number(quote.total) || 0, creditNote: quote.credit_note ?? null });
      const { error } = await supabase.rpc("pegasus_plan_sale_on_invoice", {
        p_custno: quote.custno, p_locno: quote.locno, p_invoice_id: invoiceId, p_term: quote.term, p_base_id: quote.base_id,
        p_total: Number(quote.total) || 0, p_via: "plan_builder", p_sale_id: quote.id, p_second_system: quote.second_system,
      });
      if (error) throw error;
      if (startDate) {
        try { await supabase.from("pegasus_plan_sale").update({ start_date: startDate }).eq("id", quote.id).eq("status", "accepted"); } catch (e) { console.error("start date not saved:", e); }
      }
      return invoiceId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pegasus_quotes"] });
      qc.invalidateQueries({ queryKey: ["pegasus_agreements"] });
      qc.invalidateQueries({ queryKey: ["plan_offers"] });
      qc.invalidateQueries({ queryKey: ["customer_proposals"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["tuneups_due"] });
      qc.invalidateQueries({ queryKey: ["membership_card"] });
      qc.invalidateQueries({ queryKey: ["customer_renewal"] });
    },
  });
}

function SavedQuotesCard({ quotes }: { quotes: PegasusQuote[] }) {
  return (
    <Card className="overflow-hidden border-dashed">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
        <FileText className="h-4 w-4 text-slate-400" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Saved Quotes</span>
        <span className="ml-auto text-[11px] text-slate-400">priced, not yet sold</span>
      </div>
      <div className="divide-y divide-slate-100">
        {quotes.map((q) => <QuoteRow key={q.id} q={q} />)}
      </div>
    </Card>
  );
}

function QuoteRow({ q }: { q: PegasusQuote }) {
  // 360-AUDIT wave 2 (C): selling a plan bills a customer — gate it.
  const canAgree = useCan("agreements.edit");
  const promote = usePromoteQuote();
  const navigate = useNavigateWithBack();
  const [selling, setSelling] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-800">{q.base_name || "PLUS plan"} · {q.term} yr{q.second_system ? " · 2 systems" : ""}</div>
          <div className="text-[11px] text-slate-400">Quoted {new Date(q.created_at).toLocaleDateString()} · {money(q.total)}</div>
        </div>
        {!selling && canAgree && <Button size="sm" onClick={() => setSelling(true)}><ShieldCheck className="h-4 w-4" /> Sell this plan</Button>}
      </div>
      {selling && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-brand-100 bg-brand-50/40 p-3">
          <label className="flex flex-col gap-1 text-[11px] font-medium text-slate-500">
            Start date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-md border border-hairline bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-brand-400 focus:outline-none" />
          </label>
          <div className="max-w-[240px] text-[11px] text-slate-500">Makes the plan&apos;s invoice; coverage runs <strong>{q.term} year{q.term === 1 ? "" : "s"}</strong> from this date once the invoice is paid, and its tune-ups get laid out then.</div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setSelling(false)} className="text-xs font-medium text-slate-400 hover:text-slate-600">Cancel</button>
            <Button size="sm" disabled={promote.isPending || !startDate} onClick={() => promote.mutate({ quote: q, startDate }, { onSuccess: (invoiceId) => { if (invoiceId) navigate(`/invoices/${invoiceId}`); } })}>
              {promote.isPending ? "Selling…" : <>Sell &amp; collect <ArrowRight className="h-3.5 w-3.5" /></>}
            </Button>
          </div>
          {promote.isError && (
            <p className="w-full text-xs font-medium text-rose-600">The sale didn&apos;t save — nothing was created. Try again.</p>
          )}
        </div>
      )}
    </div>
  );
}

// SELL-01 — Pegasus-native sold plans (the agreement builder's output). These
// live entirely in Pegasus (pegasus_plan_sale, status='sold'); ESC is never
// touched. They render as their own cards, badged "Created in Pegasus", and
// reuse the derived membership timeline (start_date = joined).
interface PegasusAgreement {
  id: string;
  locno: string | null;
  term: number;
  base_name: string | null;
  addon_names: string[];
  item_lines: { name: string; qty: number; price: number; custom?: boolean }[];
  second_system: boolean;
  total: number;
  start_date: string | null;
  expire_date: string | null;
  sold_by: string | null;
  status: string;
}

function usePegasusAgreements(custno: string | undefined, locno?: string | null) {
  return useQuery({
    queryKey: ["pegasus_agreements", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PegasusAgreement[]> => {
      let q = supabase
        .from("pegasus_plan_sale")
        .select("id, locno, term, base_name, addon_ids, item_lines, second_system, total, start_date, expire_date, sold_by, sold_at, status")
        .eq("custno", custno as string)
        .in("status", ["sold", "cancelled"])
        .order("sold_at", { ascending: false });
      if (locno) q = q.eq("locno", locno);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as Record<string, unknown>[];
      // Resolve add-on ids → names (incl. since-archived rows) for display.
      const allAddonIds = [...new Set(rows.flatMap((r) => (r.addon_ids as string[]) ?? []))];
      const nameById = new Map<string, string>();
      if (allAddonIds.length) {
        const { data: prices } = await supabase.from("pegasus_plan_price").select("id, name").in("id", allAddonIds);
        for (const p of (prices ?? []) as { id: string; name: string }[]) nameById.set(p.id, p.name);
      }
      return rows.map((r) => ({
        id: r.id as string,
        locno: (r.locno as string) ?? null,
        term: Number(r.term ?? 0),
        base_name: (r.base_name as string) ?? null,
        addon_names: ((r.addon_ids as string[]) ?? []).map((id) => nameById.get(id) ?? id),
        item_lines: (r.item_lines as PegasusAgreement["item_lines"]) ?? [],
        second_system: !!r.second_system,
        total: Number(r.total ?? 0),
        start_date: (r.start_date as string) ?? null,
        expire_date: (r.expire_date as string) ?? null,
        sold_by: (r.sold_by as string) ?? null,
        status: (r.status as string) ?? "sold",
      }));
    },
  });
}

function PegasusAgreementCard({ ag, custno, onBuild, priorPlan }: { ag: PegasusAgreement; custno: string; onBuild?: () => void; priorPlan?: boolean }) {
  const canAgree = useCan("agreements.edit");
  const today = new Date().toISOString().slice(0, 10);
  const cancelled = ag.status === "cancelled";
  const active = !cancelled && !!ag.expire_date && ag.expire_date >= today;
  const { data: tiers } = usePlanDiscounts();
  const repair = repairPctForTerm(tiers, ag.term);
  const [manageOpen, setManageOpen] = useState(false);
  const [tab, setTab] = useState<"overview" | "tuneups" | "equipment">("overview");
  const { data: equipRows } = useCustomerEquipment(custno, ag.locno);
  const { data: equipOverrides } = useEquipmentOverrides(custno, ag.locno);
  const equipView = buildEquipmentView(equipRows ?? [], undefined, equipOverrides ?? undefined);
  // PLAN-CARD-01: the same header as the ESC card — facts from the visit ledger.
  const { data: visitRows } = usePlanVisitRows({ saleId: ag.id });
  const vrows = visitRows ?? [];
  const vUsed = vrows.filter((r) => r.status === "completed").length;
  const vNext = vrows.find((r) => !CONSUMED.has(r.status)) ?? null;
  const leftMonths = monthsUntil(ag.expire_date);
  const daysLeft = ag.expire_date ? Math.round((new Date(ag.expire_date + "T00:00:00").getTime() - Date.now()) / 86400000) : null;
  const expiring = active && daysLeft != null && daysLeft <= 60;
  const status = cancelled ? { tone: "bad" as const, label: "Cancelled" } : expiring ? { tone: "warn" as const, label: `Expiring · ${daysLeft}d` } : active ? { tone: "good" as const, label: "Active" } : { tone: "mute" as const, label: "Expired" };
  const includedParts: IncludedPart[] = ag.item_lines.map((it) => ({ code: it.name, qty: it.qty, description: it.price ? `${money(it.price)} each` : null, item_id: null, kind: planPartKind(it.name) as PartKind, source: "pegasus" as const }));
  const facts: PlanFactSpec[] = [
    { icon: User, label: "Sold By", value: ag.sold_by ?? "Not recorded", sub: ag.start_date ? `plan started ${longDate(ag.start_date)}` : null },
    { icon: RefreshCw, label: "Last Renewed", value: priorPlan ? longDate(ag.start_date) : "First term", sub: priorPlan ? "renewed in Pegasus" : "never renewed yet" },
    { icon: ShieldCheck, label: "Tune-Ups This Term", value: vrows.length ? `${vUsed} of ${vrows.length} · ${vrows.filter((r) => !CONSUMED.has(r.status)).length} left` : "—", sub: rhythmWord(vrows.map((r) => r.task_label ?? "")) ?? "generated from the plan", tone: vrows.length && vUsed < vrows.length ? "good" : undefined },
    { icon: CalendarClock, label: "Next Check", value: vNext ? `${(vNext.task_label ?? "Tune-up").replace(/ tune-up$/i, "")}${vNext.seq_no && vNext.seq_total ? ` · ${vNext.seq_no} of ${vNext.seq_total}` : ""}` : "—", sub: vNext ? (vNext.status === "booked" || vNext.status === "scheduled" ? `booked ${longDate(vNext.booked_date)}` : `${vNext.target_date ? seasonOf(vNext.target_date) : "—"} · on the worklist`) : "nothing owed" },
    { icon: Clock, label: active ? "Expires" : cancelled ? "Cancelled" : "Expired", value: longDate(ag.expire_date), sub: active ? `${leftMonths ?? 0} months · renews at ${money(ag.total)}` : null, tone: expiring ? "warn" : undefined },
  ];

  return (
    <Card className={cn("overflow-hidden", !active && "opacity-80")}>
      <PlanHeader
        name={`${ag.term}-Year Plan`}
        active={active}
        status={status}
        chip={<span className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-bold text-violet-700"><Sparkles className="h-3 w-3" /> Sold in Pegasus</span>}
        moneyLine={<>
          <b className="font-bold text-slate-900">{money(ag.total)}</b> for {termWords(ag.term)}
          {perYearWord(ag.total, ag.term) && <><Dot /><span className="tabular-nums">{perYearWord(ag.total, ag.term)}</span></>}
          <Dot /><span className="tabular-nums">{longDate(ag.start_date)} → {longDate(ag.expire_date)}</span>
          {ag.base_name && <><Dot /><span>{ag.base_name}{ag.addon_names.length ? ` + ${ag.addon_names.join(" + ")}` : ""}{ag.second_system ? " · 2nd system" : ""}</span></>}
        </>}
        facts={facts}
        onManage={() => setManageOpen(true)}
        onBuild={onBuild}
        canEdit={canAgree}
      />

      {/* The same sub-tabs as an ESC plan — one card, one way (Seth: "when
          pegasus agreements start being entered they work and all flow the same"). */}
      <div className="border-b border-slate-100 px-6 py-3.5">
        <div className="inline-flex flex-wrap gap-0.5 rounded-xl bg-slate-100 p-[3px]">
          {([["overview", "Overview"], ["tuneups", "Tune-Ups"], ["equipment", "What's Covered"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={cn("rounded-[10px] px-4 py-2 text-[13px] font-bold transition-colors", tab === k ? "bg-white text-brand-700 shadow-sm" : "text-slate-600 hover:text-slate-900")}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <>
          <MembershipTimeline joined={ag.start_date} renewed={null} expire={ag.expire_date} active={active} />
          <div className="space-y-6 px-6 pb-5 pt-4">
            <PlanGlance custno={custno} locno={ag.locno} taskLabels={vrows.map((r) => r.task_label ?? "").filter(Boolean)} includedParts={includedParts} termYears={ag.term} />
            <PlanAdjustmentsLedger custno={custno} locno={ag.locno} plan={{ kind: "pegasus", saleId: ag.id }} />
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              {repair > 0 ? `${Math.round(repair * 100)}% off repairs` : "Member repair discount"} · priority service
            </div>
          </div>
        </>
      )}
      {tab === "tuneups" && (
        <div className="px-6 py-5">
          {/* MEMBERSHIPS-02 P1: the plan's visits are a ledger, not a guess —
              every check the term owes, with where it stands; unused ones read
              "forfeited" once the term ends (the nightly rule). */}
          <PlanVisitsLedger saleId={ag.id} title="This term's visits" />
          {vrows.length === 0 && <p className="text-sm text-slate-500">No visits generated for this plan yet.</p>}
        </div>
      )}
      {tab === "equipment" && (
        <WhatsCovered
          custno={custno}
          locno={ag.locno}
          plan={{ kind: "pegasus", saleId: ag.id }}
          termYears={ag.term}
          termEnd={ag.expire_date}
          taskLabels={vrows.map((r) => r.task_label ?? "").filter(Boolean)}
          nextCheck={vNext ? { label: `${(vNext.task_label ?? "Tune-up").replace(/ tune-up$/i, "")} Check`, when: vNext.target_date ? seasonOf(vNext.target_date) : "next" } : null}
          includedParts={includedParts}
          equipView={equipView}
          equipOverrides={equipOverrides}
          whoLine={ag.sold_by ? ag.sold_by.split(" ")[0] : null}
          canEdit={canAgree}
        />
      )}
      {manageOpen && (
        <ManagePlanModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          plan={{
            id: ag.id,
            custno,
            locno: ag.locno,
            base_name: ag.base_name,
            total: ag.total,
            term: ag.term,
            start_date: ag.start_date,
            expire_date: ag.expire_date,
            status: ag.status,
          }}
        />
      )}
    </Card>
  );
}

function AgreementCard({ a, extra, onBuild }: { a: AgreementView; extra?: AgrExtra; onBuild?: () => void }) {
  const canAgree = useCan("agreements.edit");
  const [tab, setTab] = useState<"overview" | "tuneups" | "equipment" | "history">("overview");
  const salesperson = extra?.salesperson ?? null;
  // PLAN-CARD-01: the plan's own included parts (ESC's agreement parts rows),
  // resolved to the pricebook — APR35 → "Water Panel Evaporator #35 Pad".
  const { data: includedParts, isLoading: includedLoading } = useIncludedParts(a.parts);
  // Last Renewed reads the renewal invoice on/near ESC's renew_date (the same
  // ledger the timeline draws from — one query, shared by key).
  const { data: billing } = useAgreementBilling(a.custno, a.agrmt_no, true);
  const renewalThresh = a.contr_amt ? a.contr_amt * 0.5 : 200;
  const renewalInvoices = (billing ?? []).filter((b) => b.amount >= renewalThresh && !!b.date);
  const renewalNear = a.renew_date
    ? renewalInvoices.find((b) => Math.abs(new Date(b.date!).getTime() - new Date(a.renew_date!).getTime()) < 45 * 86400000) ?? null
    : null;
  // Tune-up counter straight from the dispatch-note "X of Y" (the authoritative
  // ESC source). On a multi-year plan this carries the whole-plan position —
  // e.g. "1 of 6 · 5 left" — not a misleading per-year count.
  // AGR-TUNEUPS-02 (Seth 2026-09-01, Rollie/Beth): counts are per TERM now —
  // used = checks actually completed this term; a booked-but-not-done check is
  // "booked", never "used"; entitled comes from the plan's own task lines.
  const tuUsed = extra?.tuneups?.used ?? null;
  const tuEntitled = extra?.tuneups?.entitled ?? null;
  const tuOpen = extra?.tuneups?.open ?? 0;
  const tuLeft = tuEntitled != null && tuUsed != null ? Math.max(0, tuEntitled - tuUsed) : null;
  const termStart = a.renew_date ?? a.beg_date;
  const { data: visitData } = useAgreementVisits(a.custno, a.locno, a.agrmt_no, termStart);

  // AGR-08 lifecycle (renew / cancel / transfer), recorded Pegasus-native.
  const { data: lifecycleActions } = useAgreementActions(a.agrmt_no);
  const recordAction = useRecordAgreementAction();
  const undoAction = useUndoAgreementAction();
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const latestAction = lifecycleActions?.[0] ?? null;
  // Benefits Ledger: most recent completed visit per check type (visits are
  // recent-first, so the first hit per type is the latest). Derived, not flagged.
  const lastByType = new Map<string, string>();
  for (const v of visitData?.visits ?? []) {
    if (v.type && !lastByType.has(v.type)) lastByType.set(v.type, v.date);
  }
  const dueThreshold = monthsAgoISO(13);

  // Only show per-check due/last-done status on in-force agreements — a lapsed
  // plan owes nothing.
  const trackStatus = a.status === "active" || a.status === "expiring";
  // The generated ledger (TUNEUPS-FACT-FROM-LEDGER) — read once here for the
  // header fact, the Last Done dates and the Tune-Ups tab.
  const { data: escLedgerEarly } = usePlanVisitRows(trackStatus ? { esc: { custno: a.custno, agrmtNo: a.agrmt_no, locno: a.locno, termStart: a.beg_date } } : {});
  // LEDGER-DATES: a completed ledger visit carries the day the tech was there;
  // ESC's $0 visit invoices post months later, so the ledger's date wins.
  const ledgerLast = new Map<string, string>();
  for (const r of escLedgerEarly ?? []) {
    if (r.status !== "completed" || !r.completed_date || !r.task_label) continue;
    const cur = ledgerLast.get(r.task_label);
    if (!cur || r.completed_date > cur) ledgerLast.set(r.task_label, r.completed_date);
  }
  for (const [label, d] of ledgerLast) lastByType.set(label, d);

  // Confident count: of the N checks the plan includes per year, how many have
  // actually been done this service year (typed visits since the anniversary).
  // We trust this (derived from real visits); a precise lifetime "check 14 of
  // 18" is NOT trustworthy from ESC data — see AGR-07 / numbering note.
  const syStart = termStart ? serviceYearStartISO(termStart) : null;

  // AGR-03b — human overrides for the current service year (defer/waive/etc.).
  const { data: overrides } = useAgreementOverrides(a.custno, a.locno, a.agrmt_no);
  const setOverride = useSetVisitOverride(a.custno, a.locno, a.agrmt_no);
  const clearOverride = useClearVisitOverride(a.custno, a.locno, a.agrmt_no);
  const [managing, setManaging] = useState<AgrTaskView | null>(null);
  const overrideByLabel = new Map<string, VisitOverride>();
  for (const o of overrides ?? []) {
    if (o.task_label && o.term_start === syStart) overrideByLabel.set(o.task_label, o);
  }

  // AGR-03c — replacement-aware nudges. If a covered unit was recently replaced
  // and its check is still owed (and undecided), suggest moving or skipping it.
  // Propose-not-auto: the dispatcher clicks; we never change it silently.
  const { data: equipRows } = useCustomerEquipment(a.custno, a.locno);
  const { data: equipOverrides } = useEquipmentOverrides(a.custno, a.locno);
  const equipView = buildEquipmentView(equipRows ?? [], undefined, equipOverrides ?? undefined);
  // WHAT'S COVERED (34i91): systems · parts · accessories with their state and
  // price live in WhatsCovered.tsx; the brain is planCoverage.ts.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const nudges: { check: AgrTaskView; replacedDate: string; other: string | null }[] = [];
  if (trackStatus && syStart) {
    const view = equipView;
    const recent = monthsAgoISO(14);
    const replacedByCat = new Map<EquipCategory, string>();
    for (const e of view.replaced) {
      if (e.replacedDate && e.replacedDate >= recent && !replacedByCat.has(e.category)) {
        replacedByCat.set(e.category, e.replacedDate);
      }
    }
    if (replacedByCat.size > 0) {
      for (const t of a.tasks) {
        if (dismissed.has(t.label) || overrideByLabel.has(t.label)) continue;
        const last = lastByType.get(t.label) ?? null;
        if (last && last >= dueThreshold) continue; // not owed
        const hit = checkCategories(t.label).find((c) => replacedByCat.has(c));
        if (!hit) continue;
        nudges.push({
          check: t,
          replacedDate: replacedByCat.get(hit)!,
          other: a.tasks.find((x) => x.label !== t.label)?.label ?? null,
        });
      }
    }
  }
  const suggest = (n: (typeof nudges)[number], status: OverrideStatus) =>
    setOverride.mutate({
      taskLabel: n.check.label,
      serviceYearStart: syStart!,
      status,
      coversCategory: status === "substituted" ? n.other : null,
      reason:
        status === "substituted"
          ? `${n.check.label} unit replaced ${fmt(n.replacedDate)} — moved to ${n.other} (from suggestion)`
          : `${n.check.label} unit replaced ${fmt(n.replacedDate)} — covered by new-equipment warranty (from suggestion)`,
    });

  // TUNEUPS-FACT-FROM-LEDGER: the generated visit ledger is the truth for the
  // term's count once it exists (after cutover nobody updates ESC's "X of Y");
  // the ESC note-count RPC is the fallback for plans with no ledger yet.
  const escLedgerRows = escLedgerEarly ?? [];
  const ledgerUsed = escLedgerRows.filter((r) => r.status === "completed").length;
  const ledgerLeft = escLedgerRows.filter((r) => !CONSUMED.has(r.status)).length;
  const ledgerNext = escLedgerRows.find((r) => !CONSUMED.has(r.status)) ?? null;
  // PLAN-CARD-01 — the header facts.
  const termYears = termYearsOf(a.contr_per, a.agrmt_type);
  const active = a.status === "active" || a.status === "expiring";
  // MEMBERSHIP-ESC-FULFILLED: paid through its expire date, every check used —
  // in force for pricing and the timeline, nothing left to track or book.
  const fulfilled = a.status === "fulfilled";
  const inForce = active || fulfilled;
  const nextSeason = a.next_post_date ? seasonOf(a.next_post_date) : null;
  const nextMonth = a.next_post_date ? parseInt(a.next_post_date.slice(5, 7), 10) : null;
  const taskLabels = a.tasks.map((t) => t.label);
  const nextCheck = nextMonth == null ? null
    : nextMonth >= 3 && nextMonth <= 8
      ? (taskLabels.find((l) => /^A\/C|^Mini/.test(l)) ?? taskLabels[0] ?? "Tune-up")
      : (taskLabels.find((l) => /^Furnace|^Boiler/.test(l)) ?? taskLabels[0] ?? "Tune-up");
  const expMonths = monthsUntil(a.expire_date);
  const facts: PlanFactSpec[] = [
    { icon: User, label: "Sold By", value: salesperson ?? "Not recorded", sub: a.orig_contr ? `member since ${monthYear(a.orig_contr)}` : null },
    {
      icon: RefreshCw, label: "Last Renewed",
      value: a.renew_date ? longDate(a.renew_date) : "First term",
      sub: a.renew_date
        ? `${money(renewalNear?.amount ?? a.contr_amt)}${renewalInvoices.length ? ` · ${renewalInvoices.length} renewal${renewalInvoices.length === 1 ? "" : "s"} on record` : ""}`
        : "never renewed yet",
    },
    ...(fulfilled ? [{
      icon: ShieldCheck, label: "Tune-Ups This Term",
      value: "All used · 0 left",
      sub: rhythmWord(taskLabels) ?? "every check of the term done",
      tone: "warn" as FactTone,
    }] : escLedgerRows.length > 0 ? [{
      icon: ShieldCheck, label: "Tune-Ups This Term",
      value: `${ledgerUsed} of ${escLedgerRows.length} · ${ledgerLeft} left`,
      sub: rhythmWord(taskLabels),
      tone: (ledgerLeft > 0 ? "good" : "warn") as FactTone,
    }] : tuEntitled != null ? [{
      icon: ShieldCheck, label: "Tune-Ups This Term",
      value: `${tuUsed ?? 0} of ${tuEntitled}${tuLeft != null ? ` · ${tuLeft} left` : ""}`,
      sub: rhythmWord(taskLabels),
      tone: (tuLeft && tuLeft > 0 ? "good" : "warn") as FactTone,
    }] : []),
    // Next Check reads the ledger's first owed number (ONE SEQUENCE, 34i93) —
    // "Furnace · 4 of 6 · Fall 2026"; ESC's next_post_date is the fallback.
    fulfilled ? {
      icon: CalendarClock, label: "Next Check",
      value: "—",
      sub: "none left on this plan · renew to continue",
      tone: "mute" as FactTone,
    } : ledgerNext && active ? {
      icon: CalendarClock, label: "Next Check",
      value: `${(ledgerNext.task_label ?? "Tune-up").replace(/ (tune-up|check)$/i, "")}${ledgerNext.seq_no && ledgerNext.seq_total ? ` · ${ledgerNext.seq_no} of ${ledgerNext.seq_total}` : ""}`,
      sub: ledgerNext.status === "booked" || ledgerNext.status === "scheduled"
        ? `booked ${longDate(ledgerNext.booked_date)}`
        : `${ledgerNext.target_date ? seasonOf(ledgerNext.target_date) : "—"}${ledgerNext.esc_dispatch ? ` · ESC posted #${ledgerNext.esc_dispatch}` : ""}`,
    } : {
      icon: CalendarClock, label: "Next Check",
      value: active && nextCheck && nextSeason ? `${nextCheck.replace(/ (tune-up|check)$/i, "")} · ${nextSeason}` : "—",
      sub: active && a.next_post_date ? `ESC posts it ${longDate(a.next_post_date)}` : active ? "nothing posted" : "plan not in force",
      tone: active ? undefined : "mute" as FactTone,
    },
    {
      icon: Clock, label: a.status === "expired" ? "Expired" : a.status === "inactive" ? "Inactive" : fulfilled ? "Paid Through" : "Expires",
      value: longDate(a.expire_date),
      sub: a.status === "expiring" && a.daysToExpire != null ? `${a.daysToExpire} days · renews at ${money(a.contr_amt)}`
        : fulfilled ? `${expMonths ?? 0} months left · renews at ${money(a.contr_amt)}`
        : active ? `${expMonths ?? 0} months · renews at ${money(a.contr_amt)}`
        : a.expire_date ? `${monthsSince(a.expire_date) ?? 0} mo ago` : null,
      tone: a.status === "expiring" || fulfilled ? "warn" : !active ? "mute" : undefined,
    },
  ];

  return (
    <>
    <Card className={cn("overflow-hidden", a.status === "expired" || a.status === "inactive" ? "opacity-80" : "")}>
      <PlanHeader
        name={a.planName}
        active={inForce}
        status={{ tone: a.status === "active" ? "good" : a.status === "expiring" || fulfilled ? "warn" : "mute", label: a.statusLabel }}
        chip={<span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] font-bold text-slate-500" title="ESC agreement number">#{a.agrmt_no}</span>}
        moneyLine={<>
          <b className="font-bold text-slate-900">{money(a.contr_amt)}</b> for {termWords(termYears)}
          {perYearWord(a.contr_amt, termYears) && <><Dot /><span className="tabular-nums">{perYearWord(a.contr_amt, termYears)}</span></>}
          <Dot /><span className="tabular-nums">{longDate(a.renew_date ?? a.beg_date)} → {longDate(a.expire_date)}</span>
          {systemsWord(taskLabels) && <><Dot /><span>{systemsWord(taskLabels)}</span></>}
        </>}
        facts={facts}
        onManage={() => setLifecycleOpen(true)}
        onBuild={onBuild}
        canEdit={canAgree}
      />

      {/* Sub-tab nav — segmented control (prominent, hard to miss) */}
      <div className="border-b border-slate-100 px-6 py-3.5">
        <div className="inline-flex flex-wrap gap-0.5 rounded-xl bg-slate-100 p-[3px]">
          {([["overview", "Overview"], ["tuneups", "Tune-Ups"], ["equipment", "What's Covered"], ["history", "History"]] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={cn("rounded-[10px] px-4 py-2 text-[13px] font-bold transition-colors", tab === k ? "bg-white text-brand-700 shadow-sm" : "text-slate-600 hover:text-slate-900")}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <>

      {/* PLAN-HISTORY — the derived membership story (tenure · renewed · status) */}
      <MembershipTimeline
        joined={a.orig_contr ?? a.beg_date}
        renewed={a.renew_date}
        expire={a.expire_date}
        active={inForce}
        period={a.contr_per}
        custno={a.custno}
        agrmtNo={a.agrmt_no}
        contrAmt={a.contr_amt}
      />

      {/* AGR-08 — last recorded lifecycle decision (renew/cancel/transfer),
          Pegasus-native + undoable. ESC's expire_date is read-only; this records
          the decision until cutover (DEC-25). */}
      {latestAction && (
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-2 text-xs">
          <span className="flex min-w-0 items-center gap-1.5 text-slate-600">
            <CalendarClock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate">
              {actionSummary(latestAction)}
              {latestAction.performed_by ? (
                <span className="text-slate-400"> · {latestAction.performed_by}</span>
              ) : null}
            </span>
          </span>
          <button
            type="button"
            onClick={() => undoAction.mutate({ id: latestAction.id, agreementNo: a.agrmt_no })}
            disabled={undoAction.isPending}
            className="inline-flex shrink-0 items-center gap-1 text-slate-400 transition-colors hover:text-slate-700 disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" /> Undo
          </button>
        </div>
      )}

      {/* AGR-03c — replacement-aware suggestions (propose-not-auto) */}
      {nudges.map((n) => (
        <div key={n.check.label} className="border-b border-amber-100 bg-amber-50/60 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-700">
                The <span className="font-medium">{n.check.label.replace(/ tune-up$/i, "")}</span> covered here looks like it was replaced {fmt(n.replacedDate)} — its tune-up may not be needed this year.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {n.other && (
                  <button
                    type="button"
                    onClick={() => suggest(n, "substituted")}
                    disabled={setOverride.isPending}
                    className="rounded-md bg-white px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-300 transition-colors hover:bg-slate-50 disabled:opacity-50"
                  >
                    Move check to {n.other.replace(/ tune-up$/i, "")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => suggest(n, "waived")}
                  disabled={setOverride.isPending}
                  className="rounded-md bg-white px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-300 transition-colors hover:bg-slate-50 disabled:opacity-50"
                >
                  Skip — new warranty
                </button>
                <button
                  type="button"
                  onClick={() => setDismissed((p) => new Set(p).add(n.check.label))}
                  className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* PLAN-CARD-01: the old Term / Started / Expires / Next service / Plan
          price grid is the header now. What the office approved lives here. */}
      <div className="space-y-6 px-6 pb-5 pt-4">
        <PlanGlance custno={a.custno} locno={a.locno} taskLabels={taskLabels} includedParts={includedParts ?? []} termYears={termYears} />
        <PlanAdjustmentsLedger custno={a.custno} locno={a.locno} plan={{ kind: "esc", agrmtNo: a.agrmt_no }} renewals={renewalInvoices.map((b) => ({ date: b.date!, amount: b.amount }))} planAmount={a.contr_amt} />
      </div>
        </>
      )}

      {tab === "tuneups" && (
        <div className="space-y-6 px-6 py-5">
          {/* MEMBERSHIPS-02 P2: the visits THIS TERM owes, generated from the plan
              (furnace each fall, A/C each spring), linked to ESC's ticket when it
              posts, flipped to booked / done by the mirror. */}
          {trackStatus && <PlanVisitsLedger title="This Term's Visits" esc={{ custno: a.custno, agrmtNo: a.agrmt_no, locno: a.locno, termStart: a.beg_date }} />}
          {/* The plan's checks — what each visit is, when it was last done, and
              the per-check Manage (defer / waive / move to the other check). */}
          {a.tasks.length > 0 && (
            <div>
              <div className="mb-2.5 flex items-center justify-between">
                <span className="text-[10.5px] font-bold uppercase tracking-[.1em] text-slate-500">The Plan's Checks</span>
                {tuEntitled != null && !escLedgerRows.length && (
                  <span className="text-xs text-slate-500">
                    <span className="font-semibold text-slate-900">{tuUsed ?? 0}</span> of {tuEntitled} used this term · {tuLeft ?? 0} left{tuOpen > 0 ? ` · ${Math.min(tuOpen, tuLeft ?? tuOpen)} posted` : ""}
                  </span>
                )}
              </div>
              <ul className="space-y-2">
                {a.tasks.map((t, i) => (
                  <TaskRow
                    key={i}
                    t={t}
                    lastDone={trackStatus ? lastByType.get(t.label) ?? null : null}
                    dueThreshold={dueThreshold}
                    trackStatus={trackStatus}
                    override={overrideByLabel.get(t.label) ?? null}
                    onManage={trackStatus && syStart ? () => setManaging(t) : undefined}
                  />
                ))}
              </ul>
            </div>
          )}
          {/* DEC-23 — derived upcoming visits from the plan recurrence; only
              when the generated ledger has nothing to say (it already lists them). */}
          {trackStatus && escLedgerRows.length === 0 && <UpcomingVisits a={a} />}
        </div>
      )}

      {/* WHAT'S COVERED (34i91) — systems · the parts the plan includes ·
          accessories & indoor air, every row with its state and its price;
          the talk card, the ghost nudge and the next ticket's lines. */}
      {tab === "equipment" && (
        <WhatsCovered
          custno={a.custno}
          locno={a.locno}
          plan={{ kind: "esc", agrmtNo: a.agrmt_no }}
          termYears={termYears}
          termEnd={a.expire_date}
          taskLabels={taskLabels}
          nextCheck={active && nextCheck && nextSeason ? { label: `${nextCheck.replace(/ (tune-up|check)$/i, "")} Check`, when: nextSeason } : null}
          includedParts={includedParts ?? []}
          includedLoading={includedLoading}
          equipView={equipView}
          equipOverrides={equipOverrides}
          whoLine={salesperson ? salesperson.split(" ")[0] : null}
          canEdit={canAgree}
        />
      )}

      {tab === "history" && <PlanServiceSection a={a} />}
    </Card>
    {managing && syStart && (
      <VisitActionModal
        check={managing}
        serviceYearStart={syStart}
        existing={overrideByLabel.get(managing.label) ?? null}
        otherChecks={a.tasks.filter((t) => t.label !== managing.label).map((t) => t.label)}
        pending={setOverride.isPending || clearOverride.isPending}
        onClose={() => setManaging(null)}
        onSubmit={(input) =>
          setOverride.mutate(input, { onSuccess: () => setManaging(null) })
        }
        onClear={
          overrideByLabel.get(managing.label) && syStart
            ? () =>
                clearOverride.mutate(
                  { taskLabel: managing.label, serviceYearStart: syStart },
                  { onSuccess: () => setManaging(null) },
                )
            : undefined
        }
      />
    )}
    {lifecycleOpen && (
      <AgreementLifecycleModal
        a={a}
        entitled={tuEntitled}
        pending={recordAction.isPending}
        onClose={() => setLifecycleOpen(false)}
        onSubmit={(input) =>
          recordAction.mutate(
            { agreementNo: a.agrmt_no, custno: a.custno, ...input },
            { onSuccess: () => setLifecycleOpen(false) },
          )
        }
      />
    )}
    </>
  );
}

// Derived visit history — truth from completed agreement-tagged visits, not the
// hand-typed "X of Y". term = since renew_date/beg_date (current contract term).
function PlanServiceSection({ a }: { a: AgreementView }) {
  const termStart = a.renew_date ?? a.beg_date;
  const { data, isLoading } = useAgreementVisits(a.custno, a.locno, a.agrmt_no, termStart);
  const navigate = useNavigateWithBack();
  const [showAll, setShowAll] = useState(false);
  if (isLoading) return <div className="h-12 animate-pulse bg-slate-50" />;
  const visits = data?.visits ?? [];
  const shown = showAll ? visits : visits.slice(0, 5);

  return (
    <div className="border-t border-slate-100 px-4 py-3">
      <div className="mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Service under this plan</span>
      </div>
      {visits.length === 0 ? (
        <p className="text-xs text-slate-500">No completed visits recorded under this plan yet.</p>
      ) : (
        <ul className="-mx-2 space-y-0.5">
          {shown.map((v) => (
            <li
              key={v.invoice}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/dispatch/${v.dispatch}`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/dispatch/${v.dispatch}`); } }}
              className="group flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-brand-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
            >
              <span className="flex items-center gap-2 text-slate-700">
                <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                {fmt(v.date)}
                {v.type && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    {(() => { const { Icon: I } = checkVisual(v.type!); const warm = v.type!.startsWith("Furnace") || v.type!.startsWith("Boiler"); return <I className={cn("h-3 w-3", warm ? "text-orange-500" : "text-slate-400")} />; })()}
                    {v.type}
                  </span>
                )}
              </span>
              <span className="flex items-center gap-3">
                <span className={cn("text-xs font-medium", v.covered ? "text-emerald-700" : "text-slate-500")}>
                  {v.covered ? "Covered" : money(v.amount)}
                </span>
                <span className="font-mono text-xs text-slate-400">#{v.dispatch}</span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-500" />
              </span>
            </li>
          ))}
          {visits.length > 5 && (
            <li className="px-2 pt-1">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                {showAll
                  ? "Show less"
                  : `Show ${visits.length - 5} earlier · ${data?.lifetimeVisits} total under this plan`}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// DEC-23 — derived upcoming visits from the plan's recurrence schedule (not a
// posted ticket). Shows the next 1–2 checks the plan is due for, by month.
function UpcomingVisits({ a }: { a: AgreementView }) {
  const { data: upcoming } = useAgreementSchedule(a.custno, a.locno, a.agrmt_no);
  if (!upcoming || upcoming.length === 0) return null;
  const next = upcoming.slice(0, 2);
  return (
    <div className="border-t border-slate-100 px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Upcoming visits</span>
        <span className="text-[11px] text-slate-400">derived from the plan schedule · no ticket needed</span>
      </div>
      <ul className="space-y-1.5">
        {next.map((u, i) => {
          const { Icon, fg, bg } = checkVisual(u.label);
          return (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span className={cn("flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md", bg, fg)}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="font-medium text-slate-900">{u.label}</span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1 text-slate-600">
                <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                {u.when}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TaskRow({
  t,
  lastDone,
  dueThreshold,
  trackStatus,
  override,
  onManage,
}: {
  t: AgrTaskView;
  lastDone: string | null;
  dueThreshold: string;
  trackStatus: boolean;
  override: VisitOverride | null;
  onManage?: () => void;
}) {
  const { Icon, fg, bg } = checkVisual(t.label);
  // A live override replaces the derived Due (a human decided this check's fate).
  const due = trackStatus && !override && (!lastDone || lastDone < dueThreshold);
  const warn = due || override?.status === "deferred";
  return (
    <li className="flex items-start gap-3">
      <span className={cn(
        "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg",
        warn ? "bg-amber-50 text-amber-600" : cn(bg, fg),
      )}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{t.label}</span>
          {t.timeMin && <span className="text-xs text-slate-400">{t.timeMin} min</span>}
          {override ? (
            <OverrideBadge o={override} />
          ) : (
            trackStatus && due && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">Due</span>
            )
          )}
        </div>
        {/* Benefits Ledger: when this check was last actually performed (derived
            from completed visits), not the hand-typed count. */}
        {trackStatus && (
          <div className="text-xs text-slate-500">
            {lastDone ? (
              <>Last done {fmt(lastDone)}</>
            ) : (
              <span className="text-amber-700">No record under this plan yet</span>
            )}
          </div>
        )}
        {override?.reason && (
          <div className="mt-0.5 text-xs italic text-slate-500">
            &ldquo;{override.reason}&rdquo;{override.updated_by ? ` — ${override.updated_by}` : ""}
          </div>
        )}
        {t.rawDesc && (
          // Strip the hand-typed leading "N of M " — usage is derived from
          // actual visits (feedback_derive_dont_flag); keep the "what's included"
          // remainder (e.g. "PLUS FRN CK, HUMI, APR35").
          <div className="truncate text-xs text-slate-400">
            {t.rawDesc.replace(/^\d+\s+of\s+\d+\s*/i, "")}
          </div>
        )}
      </div>
      {onManage && (
        <button
          type="button"
          onClick={onManage}
          className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
        >
          {override ? "Edit" : "Manage"}
        </button>
      )}
    </li>
  );
}

function OverrideBadge({ o }: { o: VisitOverride }) {
  const m =
    o.status === "waived"
      ? { text: "Skipped", cls: "bg-slate-100 text-slate-600" }
      : o.status === "deferred"
        ? { text: o.deferred_to ? `Rescheduled to ${fmt(o.deferred_to)}` : "Rescheduled", cls: "bg-amber-50 text-amber-700" }
        : o.status === "substituted"
          ? { text: o.covers_category ? `Moved to ${o.covers_category}` : "Moved to another unit", cls: "bg-slate-100 text-slate-600" }
          : o.status === "carried_over"
            ? { text: "Rolled to next year", cls: "bg-slate-100 text-slate-600" }
            : { text: o.status, cls: "bg-slate-100 text-slate-600" };
  return <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", m.cls)}>{m.text}</span>;
}

// Small "i" that reveals a real-world example on hover/focus — so the rarer
// actions don't need memorizing. Hover-only span (no interactive descendants),
// safe to sit inside the action button.
function InfoTip({ title, example }: { title: string; example: string }) {
  return (
    <span className="group/info relative inline-flex flex-shrink-0" tabIndex={0}>
      <Info className="h-3.5 w-3.5 text-slate-400 transition-colors group-hover/info:text-brand-600" />
      <span className="pointer-events-none absolute right-0 top-5 z-20 hidden w-56 rounded-lg border border-slate-200 bg-white p-2.5 text-left shadow-lg group-hover/info:block group-focus/info:block">
        <span className="block text-xs font-semibold text-slate-900">{title}</span>
        <span className="mt-1 block text-xs leading-snug text-slate-600">{example}</span>
      </span>
    </span>
  );
}

// AGR-03b — the first write-from-a-button UI. Records a human decision about a
// check this service year; writes an audited override row (Supabase only).
function VisitActionModal({
  check,
  serviceYearStart,
  existing,
  otherChecks,
  pending,
  onClose,
  onSubmit,
  onClear,
}: {
  check: AgrTaskView;
  serviceYearStart: string;
  existing: VisitOverride | null;
  otherChecks: string[];
  pending: boolean;
  onClose: () => void;
  onSubmit: (i: SetOverrideInput) => void;
  onClear?: () => void;
}) {
  const [status, setStatus] = useState<OverrideStatus>(existing?.status ?? "deferred");
  const [reason, setReason] = useState(existing?.reason ?? "");
  const [deferredTo, setDeferredTo] = useState(existing?.deferred_to ?? "");
  const [target, setTarget] = useState(existing?.covers_category ?? otherChecks[0] ?? "");

  // Common two first; plain dispatcher language. The "i" reveals a real example.
  const ACTIONS: { key: OverrideStatus; label: string; help: string; example: string }[] = [
    {
      key: "deferred",
      label: "Reschedule",
      help: "Move to a later date this year",
      example: "Mrs. Olson is in Arizona until November — push her furnace check to then. Still owed, just later.",
    },
    {
      key: "waived",
      label: "Skip",
      help: "Won't be done — stops showing as owed",
      example: "Customer is selling in two months and declined the A/C check. Skip it so it stops showing overdue.",
    },
    {
      key: "substituted",
      label: "Move to Another Unit",
      help: "Apply this visit to different equipment",
      example: "They replaced the A/C in January (brand new, under warranty) — move that visit to the furnace so the plan value isn't wasted.",
    },
    {
      key: "carried_over",
      label: "Roll to Next Year",
      help: "Unused this year — added to next term",
      example: "They never used their second tune-up and just renewed — roll it into next year as goodwill.",
    },
  ];

  const submit = () =>
    onSubmit({
      taskLabel: check.label,
      serviceYearStart,
      status,
      reason,
      deferredTo: status === "deferred" ? deferredTo || null : null,
      coversCategory: status === "substituted" ? target || null : null,
    });

  return (
    <Modal open onClose={onClose} title={`Manage ${check.label}`} subtitle="This service year" size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {ACTIONS.map((act) => (
            <button
              key={act.key}
              type="button"
              onClick={() => setStatus(act.key)}
              className={cn(
                "rounded-lg border p-2.5 text-left transition-colors",
                status === act.key ? "border-brand-500 bg-brand-50" : "border-slate-200 hover:border-slate-300",
              )}
            >
              <div className="flex items-start justify-between gap-1">
                <span className="text-sm font-medium text-slate-900">{act.label}</span>
                <InfoTip title={act.label} example={act.example} />
              </div>
              <div className="mt-0.5 text-xs text-slate-500">{act.help}</div>
            </button>
          ))}
        </div>

        {status === "deferred" && (
          <label className="block text-sm">
            <span className="text-slate-600">Reschedule to</span>
            <input
              type="date"
              value={deferredTo}
              onChange={(e) => setDeferredTo(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
            {/* Quick pushes — one click instead of date math. */}
            <span className="mt-1.5 flex flex-wrap gap-1.5">
              {([["+1 month", 1], ["+3 months", 3], ["Next season (+6 mo)", 6]] as const).map(([lbl, months]) => (
                <button
                  key={lbl}
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() + months);
                    setDeferredTo(d.toISOString().slice(0, 10));
                  }}
                  className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
                >
                  {lbl}
                </button>
              ))}
            </span>
          </label>
        )}
        {status === "substituted" && otherChecks.length > 0 && (
          <label className="block text-sm">
            <span className="text-slate-600">Move to</span>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            >
              {otherChecks.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
        )}

        <label className="block text-sm">
          <span className="text-slate-600">Reason <span className="text-slate-400">(saved with your name)</span></span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Replaced the furnace under warranty instead of tuning"
            className="mt-1 block w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </label>

        <div className="flex items-center justify-between gap-2">
          <div>
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                disabled={pending}
                className="text-xs font-medium text-slate-500 transition-colors hover:text-red-600 disabled:opacity-50"
              >
                Clear override
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={pending || !reason.trim()}>
              {pending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// AGR-08 — summarize a recorded lifecycle action for the card strip.
function actionSummary(act: AgreementActionRow): string {
  const eff = act.effective_date ? fmt(act.effective_date) : "";
  if (act.action_type === "renew") {
    const d = act.detail as { renewed_to?: string; added_visits?: number } | null;
    const to = d?.renewed_to;
    const visits = d?.added_visits;
    return `Renewal recorded${visits ? ` — +${visits} tune-up${visits === 1 ? "" : "s"}` : ""}${to ? `, extends to ${fmt(to)}` : ""}${eff ? ` · ${eff}` : ""}`;
  }
  if (act.action_type === "cancel") {
    return `Cancellation recorded${act.reason ? ` — ${act.reason}` : ""}${eff ? ` · ${eff}` : ""}`;
  }
  if (act.action_type === "transfer") {
    const to = (act.detail as { to_custno?: string } | null)?.to_custno;
    return `Transfer recorded${to ? ` — to #${to}` : ""}${eff ? ` · ${eff}` : ""}`;
  }
  return `${act.action_type} recorded${eff ? ` · ${eff}` : ""}`;
}

// AGR-08 — the agreement lifecycle toolkit (renew / cancel / transfer). Review-
// before-commit: Renew shows the before→after expiry, Cancel needs a reason,
// Transfer takes the receiving account. Records Pegasus-native (DEC-25); ESC
// stays read-only. Create + member-messaging are the next slices.
/** expire + N months, date-safe. */
function addMonthsISO(dateISO: string | null, months: number): string | null {
  if (!dateISO) return null;
  const d = new Date(dateISO + "T00:00:00");
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

// EXTEND-GRANULAR v2 (Seth 2026-08-18 round 2: "We have 1/2/3/5/10 year
// plans. How do we handle extending 2 years?"): a QUANTITY × UNIT control
// instead of three fixed buttons. Pick tune-ups, years, or full terms, then
// step the number — 2 years on a 10-year plan, 3 checks, anything. Months +
// visits compute from the plan's own cadence (entitled ÷ term years).
type ExtendUnit = "visits" | "years" | "terms";

function AgreementLifecycleModal({
  a,
  entitled,
  pending,
  onClose,
  onSubmit,
}: {
  a: AgreementView;
  entitled: number | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (i: {
    actionType: AgreementActionType;
    reason?: string | null;
    detail?: Record<string, unknown> | null;
    effectiveDate?: string | null;
  }) => void;
}) {
  const [action, setAction] = useState<AgreementActionType>("renew");
  const [reason, setReason] = useState("");
  const [toCustno, setToCustno] = useState("");
  const [toPicked, setToPicked] = useState<PickedCustomer | null>(null);
  const [extendUnit, setExtendUnit] = useState<ExtendUnit>("terms");
  const [extendQty, setExtendQty] = useState(1);

  const termYears = a.contr_per ? parseInt(a.contr_per, 10) || 1 : 1;
  const visitsPerYear =
    entitled && termYears ? Math.max(1, Math.round(entitled / termYears)) : 2;
  const monthsPerVisit = Math.max(1, Math.round(12 / visitsPerYear));
  const fullTermVisits = entitled ?? visitsPerYear * termYears;

  const UNIT_META: Record<ExtendUnit, { label: string; months: number; visits: number }> = {
    visits: { label: "Tune-ups", months: monthsPerVisit, visits: 1 },
    years: { label: "Years", months: 12, visits: visitsPerYear },
    terms: { label: `Full terms (${a.contr_per ?? "1 Year"})`, months: termYears * 12, visits: fullTermVisits },
  };
  const addedMonths = UNIT_META[extendUnit].months * extendQty;
  const addedVisits = UNIT_META[extendUnit].visits * extendQty;
  const renewedTo =
    extendUnit === "terms" && extendQty === 1
      ? addContractPeriod(a.expire_date, a.contr_per)
      : addMonthsISO(a.expire_date, addedMonths);
  const unitNoun =
    extendUnit === "visits"
      ? `tune-up${extendQty === 1 ? "" : "s"}`
      : extendUnit === "years"
        ? `year${extendQty === 1 ? "" : "s"}`
        : `term${extendQty === 1 ? "" : "s"}`;

  const ACTIONS: {
    key: AgreementActionType;
    label: string;
    icon: LucideIcon;
    help: string;
  }[] = [
    { key: "renew", label: "Renew", icon: RefreshCw, help: "Extend coverage another term" },
    { key: "cancel", label: "Cancel", icon: Ban, help: "End it, with a reason" },
    { key: "transfer", label: "Transfer", icon: ChevronRight, help: "Move to a new owner" },
  ];

  const canSubmit =
    action === "renew"
      ? !!renewedTo
      : action === "cancel"
        ? !!reason.trim()
        : action === "transfer"
          ? !!toCustno.trim()
          : false;

  const submit = () => {
    if (action === "renew")
      onSubmit({
        actionType: "renew",
        detail: {
          from_expire: a.expire_date,
          renewed_to: renewedTo,
          extend_unit: extendUnit,
          extend_qty: extendQty,
          added_visits: addedVisits,
          added_months: addedMonths,
        },
        reason: reason.trim() || null,
      });
    else if (action === "cancel")
      onSubmit({ actionType: "cancel", reason: reason.trim() });
    else if (action === "transfer")
      onSubmit({
        actionType: "transfer",
        detail: { to_custno: toCustno.trim() },
        reason: reason.trim() || null,
      });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Manage ${a.planName}`}
      subtitle={`#${a.agrmt_no} · ${money(a.contr_amt)}${a.contr_per ? ` · ${a.contr_per}` : ""}`}
      size="md"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {ACTIONS.map((act) => {
            const Icon = act.icon;
            return (
              <button
                key={act.key}
                type="button"
                onClick={() => setAction(act.key)}
                className={cn(
                  "rounded-lg border p-2.5 text-left transition-colors",
                  action === act.key
                    ? "border-brand-500 bg-brand-50"
                    : "border-slate-200 hover:border-slate-300",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-sm font-medium text-slate-900">
                    {act.label}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-slate-500">{act.help}</div>
              </button>
            );
          })}
        </div>

        {/* How much? quantity × unit — handles "+2 years" on any plan length. */}
        {action === "renew" && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-600">Extend by</span>
              {/* Quantity stepper */}
              <span className="inline-flex items-center rounded-lg border border-slate-200">
                <button
                  type="button"
                  onClick={() => setExtendQty((q) => Math.max(1, q - 1))}
                  disabled={extendQty <= 1}
                  className="flex h-9 w-9 items-center justify-center rounded-l-lg text-slate-500 transition-colors hover:bg-slate-50 disabled:opacity-40"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-10 text-center text-base font-bold tabular-nums text-slate-900">
                  {extendQty}
                </span>
                <button
                  type="button"
                  onClick={() => setExtendQty((q) => Math.min(20, q + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-r-lg text-slate-500 transition-colors hover:bg-slate-50"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </span>
              {/* Unit */}
              <span className="inline-flex flex-wrap gap-0.5 rounded-lg bg-slate-100 p-0.5">
                {(Object.keys(UNIT_META) as ExtendUnit[]).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setExtendUnit(u)}
                    className={cn(
                      "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                      extendUnit === u ? "bg-white text-emerald-700 shadow-sm" : "text-slate-600 hover:text-slate-900",
                    )}
                  >
                    {UNIT_META[u].label}
                  </button>
                ))}
              </span>
            </div>
            {/* Review-before-commit preview */}
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="flex items-center justify-center gap-2 text-sm text-slate-700">
                <span className="text-slate-500">{fmt(a.expire_date)}</span>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <span className="font-semibold text-emerald-700">
                  {fmt(renewedTo)}
                </span>
              </div>
              <p className="mt-1 text-center text-xs text-slate-400">
                +{extendQty} {unitNoun} = {addedVisits} tune-up{addedVisits === 1 ? "" : "s"} added
                {addedMonths ? ` over ${addedMonths} months` : ""}. Price is
                whatever you collect — record it on the renewal invoice as usual.
              </p>
            </div>
          </>
        )}
        {action === "transfer" && (
          <div className="block text-sm">
            <span className="text-slate-600">Receiving account</span>
            {/* SEARCH-FIRST (Seth 2026-08-20): find the new owner by name,
                address, or phone — no memorized customer numbers. */}
            <div className="mt-1">
              <CustomerPicker
                selected={toPicked}
                onClear={() => { setToPicked(null); setToCustno(""); }}
                onSelect={(c) => { setToPicked(c); setToCustno(c.custno); }}
                excludeCustno={a.custno}
                placeholder="Search the new owner - name, address, phone, or #"
              />
            </div>
            <span className="mt-1 block text-xs text-slate-400">
              When a home sells, move the plan to the new owner&apos;s account.
            </span>
          </div>
        )}

        <label className="block text-sm">
          <span className="text-slate-600">
            {action === "cancel" ? "Reason " : "Note "}
            <span className="text-slate-400">
              ({action === "cancel" ? "required" : "optional"}, saved with your name)
            </span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder={
              action === "cancel"
                ? "e.g. Customer sold the home and declined to transfer"
                : "Anything worth recording with this"
            }
            className="mt-1 block w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </label>

        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={pending || !canSubmit}>
            {pending
              ? "Recording…"
              : action === "renew"
                ? "Record renewal"
                : action === "cancel"
                  ? "Record cancellation"
                  : "Record transfer"}
          </Button>
        </div>
        <p className="text-center text-[11px] text-slate-400">
          Recorded in Pegasus — ESC isn&apos;t touched. Undo any time from the card.
        </p>
      </div>
    </Modal>
  );
}


/** MEMBERSHIPS-02 P1/P2 — the visits a plan owes over its term, from the
 *  generated agreement_visit rows (Pegasus sales AND ESC plans since P2):
 *  label · target month · where it stands. ESC rows carry the ESC ticket. */
interface PlanVisitRow { id: string; custno: string; locno: string | null; plan_sale_id: string | null; task_label: string | null; covers_category: string | null; sequence_label: string | null; status: string; target_date: string | null; completed_date: string | null; booked_date: string | null; esc_dispatch: string | null; fulfilled_dispatch: string | null; reason: string | null; term_start: string | null; term_end: string | null; seq_no: number | null; seq_total: number | null; updated_by?: string | null }
/** Checks a term no longer owes — used (completed) or consumed without a visit. */
const CONSUMED = new Set(["completed", "forfeited", "skipped", "declined", "cancelled", "missed"]);
interface PlanVisitScope { saleId?: string; esc?: { custno: string; agrmtNo: string; locno: string | null; termStart: string | null } }
/** The generated visit ledger for a plan — shared by the ledger list and the card's facts. */
function usePlanVisitRows({ saleId, esc }: PlanVisitScope) {
  return useQuery({
    queryKey: ["plan_visits", saleId ?? `${esc?.custno}:${esc?.agrmtNo}:${esc?.locno ?? ""}:${esc?.termStart ?? ""}`],
    staleTime: 30 * 1000,
    enabled: !!saleId || !!esc,
    queryFn: async (): Promise<PlanVisitRow[]> => {
      let q = supabase
        .from("agreement_visit")
        .select("id, custno, locno, plan_sale_id, task_label, covers_category, sequence_label, status, target_date, completed_date, booked_date, esc_dispatch, fulfilled_dispatch, reason, term_start, term_end, seq_no, seq_total, updated_by")
        .eq("source", "generated")
        .order("seq_no", { ascending: true, nullsFirst: false })
        .order("target_date", { ascending: true });
      if (saleId) q = q.eq("plan_sale_id", saleId);
      else if (esc) {
        q = q.eq("custno", esc.custno).eq("agrmt_no", esc.agrmtNo).is("plan_sale_id", null);
        if (esc.termStart) q = q.eq("term_start", esc.termStart);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as PlanVisitRow[];
    },
  });
}

function PlanVisitsLedger({ saleId, esc, title = "Plan visits" }: PlanVisitScope & { title?: string }) {
  // MANAGE-PLAN-3 (Seth 2026-10-06: "where did all the options go for moving
  // checks..."): Pegasus-sold plans get per-check management right on the
  // ledger — Book (same engine as Tune-Ups Due), Move the target date, Skip.
  // ESC-era plans keep their own per-check Manage on the Tune-ups sub-tab.
  const qc = useQueryClient();
  const canAgree = useCan("agreements.edit");
  const schedule = useScheduleTuneup();
  const [movingId, setMovingId] = useState<string | null>(null);
  const [moveDate, setMoveDate] = useState("");
  const { data } = usePlanVisitRows({ saleId, esc });
  const rows = data ?? [];
  const inval = () => qc.invalidateQueries({ queryKey: ["plan_visits", saleId ?? `${esc?.custno}:${esc?.agrmtNo}:${esc?.locno ?? ""}:${esc?.termStart ?? ""}`] });
  const lastDate = rows.reduce((m, v) => ((v.target_date ?? "") > m ? (v.target_date as string) : m), "");
  const bookNow = async (r: (typeof rows)[number]) => {
    const [{ data: mc }, { data: pc }] = await Promise.all([
      supabase.from("mirror_customer").select("fullname, firstname, lastname").eq("custno", r.custno).maybeSingle(),
      supabase.from("pegasus_customer").select("fullname, firstname, lastname").eq("custno", r.custno).maybeSingle(),
    ]);
    const c = (mc ?? pc) as { fullname?: string | null; firstname?: string | null; lastname?: string | null } | null;
    const o: TuneupDueRow = {
      id: r.id, custno: r.custno, locno: r.locno, plan_sale_id: r.plan_sale_id,
      task_label: r.task_label ?? "Tune-up", covers_category: r.covers_category ?? "heating",
      sequence_label: r.sequence_label ?? (r.task_label ?? "Tune-up"), target_date: r.target_date ?? new Date().toISOString().slice(0, 10),
      status: r.status, fulfilled_dispatch: r.fulfilled_dispatch ?? null, term_start: r.term_start, term_end: r.term_end,
      is_renewal_check: (r.target_date ?? "") === lastDate,
      customer_name: c?.fullname?.trim() || `${c?.firstname ?? ""} ${c?.lastname ?? ""}`.trim() || r.custno,
    };
    schedule.mutate(o, { onSuccess: () => { inval(); qc.invalidateQueries({ queryKey: ["tuneups_due"] }); } });
  };
  const moveCheck = async (id: string) => {
    if (!moveDate) return;
    const { data: u } = await supabase.auth.getUser();
    await supabase.from("agreement_visit").update({ target_date: moveDate, updated_at: new Date().toISOString(), updated_by: u?.user?.email ?? null }).eq("id", id);
    setMovingId(null); setMoveDate(""); inval();
  };
  if (rows.length === 0) return null;
  const used = rows.filter((r) => r.status === "completed").length;
  const forfeited = rows.filter((r) => r.status === "forfeited").length;
  const left = rows.filter((r) => !CONSUMED.has(r.status)).length;
  const missed = rows.filter((r) => r.status === "missed" || r.status === "skipped").length;
  // UNUSED CHECKS (Seth 2026-10-21: "for unused checks the ability to archive
  // or mark them as missed / skipped"): the office's word on a row outranks
  // the mirror — it is stamped with the user's email, which the renumber and
  // the reconcile leave alone. Archive = forfeited, reason "archived by the office".
  const setStatus = async (r: PlanVisitRow, status: "missed" | "skipped" | "forfeited" | "open", reason: string | null) => {
    const { data: u } = await supabase.auth.getUser();
    const patch: Record<string, unknown> = { status, reason, updated_at: new Date().toISOString(), updated_by: u?.user?.email ?? "office" };
    if (status === "open") { patch.booked_date = null; }
    await supabase.from("agreement_visit").update(patch).eq("id", r.id);
    inval(); qc.invalidateQueries({ queryKey: ["tuneups_due"] }); qc.invalidateQueries({ queryKey: ["membership_status"] }); qc.invalidateQueries({ queryKey: ["quote_membership_status"] });
  };
  const [archiveOpen, setArchiveOpen] = useState(false);
  const STATUS: Record<string, { label: string; cls: string }> = {
    open: { label: "Owed", cls: "bg-slate-100 text-slate-600" },
    missed: { label: "Missed", cls: "bg-amber-50 text-amber-700" },
    invited: { label: "Invited", cls: "bg-sky-50 text-sky-700" },
    picked: { label: "Picked a Day", cls: "bg-sky-50 text-sky-700" },
    scheduled: { label: "Booked", cls: "bg-brand-50 text-brand-700" },
    booked: { label: "Booked", cls: "bg-brand-50 text-brand-700" },
    snoozed: { label: "Snoozed", cls: "bg-amber-50 text-amber-700" },
    completed: { label: "Done", cls: "bg-emerald-50 text-emerald-700" },
    skipped: { label: "Skipped", cls: "bg-slate-100 text-slate-500" },
    declined: { label: "Declined", cls: "bg-slate-100 text-slate-500" },
    forfeited: { label: "Forfeited", cls: "bg-rose-50 text-rose-700" },
    cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500" },
  };
  // LEDGER-ORDER (Seth 2026-10-21: "it's easy to miss Done when the one above
  // it is still owed"): the list reads as time — what happened, then what is
  // coming. Each row sits on the date that matters for it (done → the visit
  // day; booked → the appointment; owed → its target), past above a rule.
  const PAST = CONSUMED;
  const whenOf = (r: PlanVisitRow) => (r.status === "completed" && r.completed_date) || ((r.status === "booked" || r.status === "scheduled") && r.booked_date) || r.target_date || "";
  const ordered = [...rows].sort((a, b) => whenOf(a).localeCompare(whenOf(b)) || (a.target_date ?? "").localeCompare(b.target_date ?? ""));
  const archived = ordered.filter((r) => r.status === "forfeited" || r.status === "cancelled" || r.status === "declined");
  const past = ordered.filter((r) => PAST.has(r.status) && !archived.includes(r));
  const ahead = ordered.filter((r) => !PAST.has(r.status));
  const checkName = (r: PlanVisitRow) => {
    const seq = r.sequence_label ?? r.task_label ?? "Tune-up";
    return seq.replace(/ tune-up/i, " Check").replace(/ check\b/i, " Check");
  };
  const row = (r: PlanVisitRow) => {
    const st = STATUS[r.status] ?? { label: r.status, cls: "bg-slate-100 text-slate-600" };
    const vis = checkVisual(r.task_label ?? "");
    const when = r.status === "completed" && r.completed_date ? longDate(r.completed_date)
      : (r.status === "booked" || r.status === "scheduled") && r.booked_date ? `booked ${longDate(r.booked_date)}`
      : r.target_date ? seasonOf(r.target_date) : "—";
    const owed = r.status === "open" || r.status === "snoozed" || r.status === "invited" || r.status === "picked";
    const manageable = !!saleId && canAgree && owed;
    const done = r.status === "completed";
    const unused = r.status === "missed" || r.status === "skipped" || r.status === "forfeited" || r.status === "declined" || r.status === "cancelled";
    const quiet = "text-xs font-semibold text-slate-400 transition-colors hover:text-slate-700 disabled:opacity-50";
    return (
      <li key={r.id} className="py-1">
        <div className={cn("group grid grid-cols-[32px_minmax(0,1fr)_auto_auto] items-center gap-3 rounded-xl px-2 py-1.5", unused && "opacity-75")}>
          <span className={cn("flex h-8 w-8 items-center justify-center rounded-[9px]", done ? "bg-emerald-50 text-emerald-600" : unused ? "bg-slate-100 text-slate-400" : vis.bg + " " + vis.fg)}>
            <vis.Icon className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className={cn("block truncate text-sm font-semibold", done ? "text-slate-700" : unused ? "text-slate-500" : "text-slate-900")}>{checkName(r)}</span>
            {r.esc_dispatch && !done && !unused && <span className="block text-[11px] text-slate-400" title="ESC posted this ticket — the appointment lives there until cutover">ESC #{r.esc_dispatch}</span>}
            {unused && r.reason && <span className="block truncate text-[11px] text-slate-400">{r.reason}</span>}
          </span>
          <span className={cn("shrink-0 text-[13px] tabular-nums", done ? "font-semibold text-slate-700" : "text-slate-500")}>{when}</span>
          <span className="flex shrink-0 items-center gap-2">
            <span className={cn("rounded-full px-2.5 py-1 text-xs font-bold", st.cls)} title={r.reason ?? undefined}>{st.label}</span>
            {manageable && (<>
              <button type="button" disabled={schedule.isPending} onClick={() => bookNow(r)} title="Create the ticket now — it drops into the schedule queue" className="rounded-full bg-brand-600 px-2.5 py-1 text-xs font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-50">Book</button>
              <button type="button" onClick={() => { setMovingId(movingId === r.id ? null : r.id); setMoveDate(r.target_date ?? ""); }} title="Move this check's target date" className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50">Move</button>
            </>)}
            {canAgree && owed && (<>
              <button type="button" onClick={() => setStatus(r, "missed", "never scheduled — marked by the office")} title="The season passed and nobody scheduled it — the number is used up" className={quiet}>Missed</button>
              <button type="button" onClick={() => setStatus(r, "skipped", "customer declined / not needed")} title="Customer declined or it was not needed — the number is used up" className={quiet}>Skip</button>
              <button type="button" onClick={() => setStatus(r, "forfeited", "archived by the office")} title="Take it off the list — unused, kept in the archive" className={quiet}>Archive</button>
            </>)}
            {canAgree && unused && (
              <button type="button" onClick={() => setStatus(r, "open", null)} title="Put it back on the list as owed" className={quiet}>Bring Back</button>
            )}
          </span>
        </div>
        {movingId === r.id && (
          <div className="mt-1.5 flex items-center justify-end gap-2">
            <input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs focus:border-brand-300 focus:outline-none" />
            <button type="button" onClick={() => moveCheck(r.id)} disabled={!moveDate} className="rounded-md bg-slate-700 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-40">Move it</button>
            <button type="button" onClick={() => { setMovingId(null); setMoveDate(""); }} className="text-[11px] font-medium text-slate-400 hover:text-slate-600">Never mind</button>
          </div>
        )}
      </li>
    );
  };
  return (
    <div className={cn(saleId ? "" : "")}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[.1em] text-slate-500">
            {title}
            <HelpTip label="About these visits">Generated from the plan's term — furnace each fall, A/C each spring — and matched to ESC's tickets as they post and complete. Nobody types these.</HelpTip>
          </div>
          <div className="font-display mt-1 text-[22px] font-extrabold leading-none tracking-tight text-slate-900">
            {used} <span className="text-slate-400">of</span> {rows.length} <span className="text-base font-bold text-slate-500">used</span>
            {left > 0 && <span className="ml-3 text-base font-bold text-emerald-700">{left} left</span>}
            {missed > 0 && <span className="ml-3 text-base font-bold text-amber-700">{missed} missed</span>}
            {forfeited > 0 && <span className="ml-3 text-base font-bold text-slate-400">{forfeited} archived</span>}
          </div>
        </div>
      </div>
      <ul className="text-sm">
        {past.map(row)}
        {past.length > 0 && ahead.length > 0 && (
          <li className="flex items-center gap-3 py-2 text-[10.5px] font-bold uppercase tracking-[.1em] text-slate-400"><span className="h-px flex-1 bg-slate-200" />Coming Up<span className="h-px flex-1 bg-slate-200" /></li>
        )}
        {ahead.map(row)}
        {archived.length > 0 && (
          <li className="pt-2">
            <button type="button" onClick={() => setArchiveOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 transition-colors hover:text-slate-600">
              <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", archiveOpen && "rotate-90")} /> Archived ({archived.length})
            </button>
            {archiveOpen && <ul className="mt-1">{archived.map(row)}</ul>}
          </li>
        )}
      </ul>
    </div>
  );
}
